/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { DraggableDirective } from "app/directives/draggable.directive";

/** Add variable $ for bootstrap support. */
declare var $: any;

/**
 * Simple Popup Dialog used to confirm actions being performed by Users. 
 * 
 * Current use case is for confirming when Users are deleting "Stored Search Lists" on the
 * "Monitor Assets" Page, but designed so that it can be reuseable in other situations. 
 * 
 * Dialog Title / Message have default values that can be overriden by using the [title] and
 * [message] inputs to the Component, or by calling the "setTitle()" / "setMessage()" methods.
 * 
 * Callers will also need to handle the (confirmClicked) and (cancelClicked) outputs to at 
 * least close the Dialog.
 */
@Component({
    selector: 'ef-simple-confirm-dialog',
    templateUrl: './simple-confirm-dialog.component.html'
})
export class SimpleConfirmDialogComponent {

    /** Draggable Directive added to that the popup is draggable.  Referenced so that the position can be reset. */
    @ViewChild(DraggableDirective) draggableDirective: DraggableDirective;

    /** Dialog Title with default value */
    @Input()
    title = "Confirm";

    /** Dialog Message with default value */
    @Input()
    message = "Are you sure?";

    /** Called when the "Confirm" button is clicked */
    @Output()
    confirmClicked = new EventEmitter<void>();

    /** Called when the "Cancel" button is clicked */
    @Output()
    cancelClicked = new EventEmitter<void>();

    /**
     * Constructor
     */
    constructor() { }

    /**
     * Called to show the Dialog.
     * 
     * This also resets the "Drag" position so that it appears in the same position
     * every time it is opened. 
     */
    public showDialog(): void {
        this.draggableDirective.reset(); // Reset the Draggable Directive so that the Dialog pops up in the same place (Horizontally centered at the top of the screen)
        $("#confirmDialog").show();
    }

    /**
     * Called to hide the Dialog
     */
    public hideDialog(): void {
        $("#confirmDialog").hide();
    }

    /**
     * Called when the Cancel button (or top left X) is clicked
     * Cancels the confirmation.
     */
    onCancelClicked(): void {
        this.cancelClicked.emit();
    }

    /**
     * Called when the Confirm button is clicked.
     * Confirms the action.
     */
    onConfirmClicked(): void {
        this.confirmClicked.emit();
    }

    /**
     * Sets the Title of the Dialog to the given value.
     */
    public setTitle(title: string): void {
        // If a NULL value is passed, use the default value.
        if (title == null) {
            title = "Confirm";
        }
        this.title = title;
    }

    /**
     * Sets the Message of the Dialog to the given value.
     */
    public setMessage(message: string): void {
        // If a NULL value is passed, use the default value.
        if (message == null) {
            message = "Are you sure?";
        }
        this.message = message;
    }
    
}
